import { t } from 'i18next';
import { get, isNil, isObject, omit } from 'lodash';
import * as Yup from 'yup';
import { SurveyType } from 'shared/types';
import {
  convertQuillHTMLToText,
  convertStringWithTagsToQuillHTML,
} from 'shared/utils';

export type TemplateDescriptionFormType = {
  name?: string | null;
  desc?: string | null;
  created_at?: string | null;
  id?: string;
};

export const getSurveyTemplateBasicSchema = ({
  takenNames,
}: {
  takenNames: string[];
}) =>
  Yup.object({
    name: Yup.string()
      .trim()
      .required(`${t('forms.err_msg.template_name_required')}`)
      .min(
        3,
        `${t('forms.err_msg.name_min_char', {
          len: '3',
        })}`,
      )
      .max(
        50,
        `${t('forms.err_msg.name_max_char', {
          len: '50',
        })}`,
      )
      .test({
        test: function (name = '', context) {
          const { createError } = context;
          if (takenNames.includes(name.trim())) {
            return createError({
              message: `${t('forms.err_msg.template_name_exist')}`,
            });
          }
          return true;
        },
      }),
    desc: Yup.string()
      .nullable()
      .max(
        300,
        `${t('forms.err_msg.description_max_char', {
          len: '300',
        })}`,
      ),
  });

export const getTemplatePayload = ({
  template,
}: {
  template: SurveyType;
}): SurveyType => {
  const meta = {
    ...(template.meta || {}),
    ...(!isNil(template?.meta?.created_by) &&
    isObject(template?.meta?.created_by)
      ? { created_by: undefined }
      : {}),
  };
  const data: SurveyType = {
    ...template,
    meta: omit(meta, template?.id ? [] : ['created_by']),
    question_details: {
      ...(template.question_details || {}),
      questions:
        template?.question_details?.questions?.map(({ id, ...q }: any) => {
          const question = { ...q, ...(template?.id ? { id } : {}) };
          question.text = !convertQuillHTMLToText(question.text)
            ? question.text
            : convertQuillHTMLToText(question.text);
          // if (q.type_info?.question_type === 'MESSAGE') {
          //   question.type_info.question_type = 'TEXT';
          //   question.type_info.meta = omit(question.type_info.meta, ['value']);
          //   question.type_info.meta.readOnly = true;
          // }
          return question;
        }) || [],
    },
    is_template: true,
    template: true,
    channel_info: null,
  };
  return omit(data, [
    'id',
    'is_active',
    ...(template?.id
      ? []
      : [
          'updated_at',
          'created_at',
          'product_client_id',
          'is_recurring',
          'display_id',
        ]),
  ]);
};

export const getTemplateFormValues = ({
  template,
}: {
  template: SurveyType;
}): SurveyType => {
  const meta = {
    ...(template.meta || {}),
    ...(!isNil(template?.meta?.created_by) &&
    isObject(template?.meta?.created_by)
      ? { created_by: get(template, 'meta.created_by.display_id') }
      : {}),
  };
  const data: SurveyType = {
    ...template,
    meta,
    question_details: {
      ...(template.question_details || {}),
      questions:
        template?.question_details?.questions?.map(({ id, ...q }: any) => {
          const question = { ...q, ...(template?.id ? { id } : {}) };
          question.text = convertStringWithTagsToQuillHTML(question.text);
          return question;
        }) || [],
    },
    is_template: true,
    template: true,
    channel_info: null,
  };
  return data;
};
